import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightDots } from '@fortawesome/free-solid-svg-icons';
import { ROUTES } from '@/routes/routes';
import { useHasPermission } from '@/hooks/use-permissions';
import { emailTemplateLibraryService } from '@/services/email-template-library.service';

/**
 * "Platform updates available" — Phase 12.5C (COMMUNICATION_DESIGN §9).
 *
 * Renders NOTHING unless there is something to act on, so it cannot add noise to
 * a dashboard it was not designed into. It is also permission-aware: a role
 * without `email-template:view` would only get a 403 from the endpoint, so it is
 * not queried at all.
 *
 * `updateAvailable` is true only for templates the workspace has customized, so
 * this is empty for a workspace that has never customized anything — which is
 * the correct default and keeps the widget silent until it is relevant.
 */
export function PlatformTemplateUpdatesWidget() {
  const can = useHasPermission();
  const enabled = can('email-template:view');

  const { data = [] } = useQuery({
    queryKey: ['email-template-library', 'updates'],
    queryFn: emailTemplateLibraryService.updates,
    staleTime: 5 * 60_000,
    enabled,
  });

  if (!enabled || data.length === 0) return null;

  return (
    <div className="rounded-xl border border-accent/40 bg-accent/10 p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent/20">
          <FontAwesomeIcon icon={faArrowUpRightDots} className="h-4 w-4 text-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">
            {data.length === 1
              ? 'An email template update is available'
              : `${data.length} email template updates are available`}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Your service provider has published newer versions of templates you have customised.
            Your workspace keeps sending your versions until you review them.
          </p>
          <ul className="mt-2 space-y-0.5">
            {data.slice(0, 3).map((t) => (
              <li key={t.templateKey} className="truncate text-xs text-foreground">
                {t.name}{' '}
                <span className="text-muted-foreground">
                  (v{t.tenantVersion ?? '—'} → v{t.platformVersion ?? '—'})
                </span>
              </li>
            ))}
            {data.length > 3 && (
              <li className="text-xs text-muted-foreground">and {data.length - 3} more…</li>
            )}
          </ul>
          <Link
            to={ROUTES.SETTINGS_COMMUNICATION_TEMPLATES}
            className="mt-2 inline-flex min-h-[36px] items-center text-xs font-medium text-primary hover:underline"
          >
            Review templates
          </Link>
        </div>
      </div>
    </div>
  );
}
